import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useEffect, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { closeMenuOnScroll } from "Pages/Blood/BloodCompendium/Test/Shared";
import { validateFields } from "Pages/LIS/Toxicology/CommonFunctions";
import {
  GetNGSRunsLookup,
  GetWellNoApiCall,
  SaveRunCreation,
  SaveWellNoWithAccessions,
} from "Services/NGSRunCreation";
import { reactSelectSMStyle, styles } from "Utils/Common";

function CreateRuns() {
  const queryClient = useQueryClient();

  const initialValue = {
    selectedRun: 0,
    runName: "",
  };

  const [data, setData] = useState<any[]>([]);
  const [createRun, setCreateRun] = useState(initialValue);

  // 🔹 GET RUN LOOKUP (v3 syntax)
  const { data: runLookupData } = useQuery(
    ["ngsRunsLookup"],
    GetNGSRunsLookup,
    {
      staleTime: 5 * 60 * 1000, // optional optimization
    }
  );

  const dropDownValues = [
    { value: 0, label: "Add New Rack" },
    ...(runLookupData?.data || []),
  ];

  // 🔹 GET WELL DATA (v3 syntax)
  const { data: wellData } = useQuery(
    ["wellNo", createRun.selectedRun],
    () => GetWellNoApiCall(createRun.selectedRun),
    {
      enabled: createRun.selectedRun !== 0,
    }
  );

  // Sync API data → local state (no behavior change)
  useEffect(() => {
    if (wellData?.data?.data) {
      setData(wellData.data.data);
    }
  }, [wellData]);

  // 🔹 CREATE RUN
  const createRunMutation = useMutation(SaveRunCreation, {
    onSuccess: (res: any) => {
      if (res?.data?.statusCode === 200) {
        queryClient.invalidateQueries("ngsRunsLookup");
        setCreateRun(initialValue);
        toast.success(res?.data?.message);
      } else {
        toast.error(res?.data?.message || "Something went wrong.");
      }
    },
    onError: () => {
      toast.error("An unexpected error occurred.");
    },
  });

  // 🔹 SAVE ACCESSIONS
  const saveMutation = useMutation(SaveWellNoWithAccessions, {
    onSuccess: (res: any) => {
      if (res?.data?.statusCode === 200) {
        queryClient.invalidateQueries("ngsRunsLookup");
        setCreateRun(initialValue);
        toast.success(res?.data?.message);
      } else {
        toast.error(res?.data?.message || "Something went wrong.");
      }
    },
    onError: () => {
      toast.error("An unexpected error occurred.");
    },
  });

  const handleChange = (field: string, value: any) => {
    setCreateRun((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangeAccession = (name: string, value: string, id: number) => {
    setData((curr) =>
      curr.map((x) => (x.id === id ? { ...x, [name]: value } : x))
    );
  };

  const handleAddNewRack = () => {
    const { isValid } = validateFields({
      runName: createRun.runName,
    });

    if (!isValid) {
      toast.error("Please Fill Run Name Field");
      return;
    }

    const payload = {
      id: createRun.selectedRun,
      runName: createRun.runName,
      runDate: new Date().toISOString(),
      runId: "",
      projectId: "",
      notes: "",
    };

    createRunMutation.mutate(payload);
  };

  const handleSave = () => {
    const hasAtLeastOneAccession = data.some((x) => x.accession);

    if (!hasAtLeastOneAccession) {
      toast.error("Please fill at least one Accession No field");
      return;
    }
    const payload = {
      runId: createRun.selectedRun,
      wellnoWithAccessionRequest: data,
    };

    saveMutation.mutate(payload);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="flex-end" className="mb-4">
            <Grid item xs={12} sm={7} md={3}>
              <Select
                inputId="NGSRunCreateselectRun"
                theme={(theme) => styles(theme)}
                name="selectedRun"
                options={dropDownValues}
                styles={reactSelectSMStyle}
                value={dropDownValues.find(
                  (x: any) => x.value === createRun.selectedRun
                )}
                menuPortalTarget={document.body}
                closeMenuOnScroll={(e) => closeMenuOnScroll(e)}
                onChange={(option: any) =>
                  handleChange("selectedRun", option.value)
                }
              />
            </Grid>

            {createRun.selectedRun === 0 && (
              <>
                <Grid item xs={12} sm={7} md={3}>
                  <input
                    type="text"
                    className="form-control h-30px rounded"
                    name="runName"
                    placeholder="Input"
                    value={createRun.runName}
                    onChange={(e) => handleChange("runName", e.target.value)}
                  />
                </Grid>

                <button
                  className="btn btn-primary btn-sm fw-bold px-5 mx-3"
                  onClick={handleAddNewRack}
                  disabled={createRunMutation.isLoading}
                >
                  Add New Rack
                </button>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>

      {createRun.selectedRun !== 0 && data.length ? (
        <>
          <div className="card">
            <Box sx={{ height: "auto", width: "100%" }}>
              <div className="table_bordered overflow-hidden">
                <TableContainer
                  sx={{
                    maxHeight: "calc(100vh - 100px)",
                    "&::-webkit-scrollbar": { width: 7 },
                    "&::-webkit-scrollbar-track": { backgroundColor: "#fff" },
                    "&:hover": {
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "var(--kt-gray-400)",
                        borderRadius: 2,
                      },
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "var(--kt-gray-400)",
                      borderRadius: 2,
                    },
                  }}
                  component={Paper}
                  className="shadow-none"
                >
                  <Table
                    aria-label="sticky table collapsible"
                    className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
                  >
                    <TableHead>
                      <TableRow className="h-30px">
                        <TableCell className="min-w-200px w-200px">
                          Well No.
                        </TableCell>
                        <TableCell>Accession No</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data?.map((item: any) => (
                        <TableRow key={item.id} className="h-40px">
                          <TableCell className="min-w-200px w-200px">
                            {item.wellNo}
                          </TableCell>
                          <TableCell>
                            {
                              <input
                                type="text"
                                className="form-control h-25px rounded w-250px"
                                name="accession"
                                value={item.accession}
                                onChange={(e) =>
                                  handleChangeAccession(
                                    e.target.name,
                                    e.target.value,
                                    item?.id
                                  )
                                }
                              />
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </Box>
          </div>
          <button
            className="btn btn-primary btn-sm fw-bold px-5 mt-3"
            onClick={handleSave}
            disabled={saveMutation.isLoading}
          >
            Save
          </button>
        </>
      ) : null}
    </>
  );
}

export default CreateRuns;
